import React, { useEffect, useMemo, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';

import TransferRequestItemDetails from '../transfer-request-item-details';
import TransferStatusTag from 'components/base/transfer-status-tag';
import { ENUM_TRANSFER_STATUS, TransferRequestType } from 'models/transfer-request.model';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import {
  buildTransferRoute,
  getTransferStoreTypeFromPathname,
  normalizeTransferStoreType
} from '../store-type';
import { DefaultTransfer } from 'utils/constants';

const TransferRequestDetails = () => {
  const [transferToUpdate, setTransferToUpdate] = useState<TransferRequestType>(DefaultTransfer);
  const [isUpdateTransferDisabled, setIsUpdateTransferDisabled] = useState(false);
  const [isUpdatingTransfer, setIsUpdatingTransfer] = useState(false);
  const [loadingTransferDetails, setLoadingTransferDetails] = useState(false);
  const router = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const storeType = normalizeTransferStoreType(getTransferStoreTypeFromPathname(location.pathname));
  const transferListPath = buildTransferRoute(storeType);
  const transferTypeParam = transferToUpdate.transfer_type || storeType;
  const { activeStore } = useDashboardContext();
  const isRequestingStore = activeStore?.id === transferToUpdate.from_store_id;

  const fetchTransferDetails = async () => {
    if (!id) return;
    try {
      setLoadingTransferDetails(true);
      const response: any = await apiService.get(`main-store-transfer/${storeType}/${id}`, {
        version: 'v2'
      });
      const transferDetails = response?.data?.data;
      if (transferDetails) {
        setTransferToUpdate(transferDetails);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching transfer request details'}
          type='error'
        />
      );
    } finally {
      setLoadingTransferDetails(false);
    }
  };

  useEffect(() => {
    fetchTransferDetails();
  }, [id, storeType]);

  const sendPayload = useMemo(
    () => ({
      items: transferToUpdate.items
        .flatMap((item) =>
          (item.batches ?? []).map((batch: any) => ({
            request_item_id: item.id,
            quantity_sent: Number(batch.quantity) || 0,
            batch_no: Number(batch.batch_no) || 0
          }))
        )
        .filter((item) => item.quantity_sent > 0)
    }),
    [transferToUpdate.items]
  );

  const receivePayload = useMemo(
    () => ({
      items: transferToUpdate.items.flatMap((item) => {
        return (item.batches ?? [])
          .map((batch: any) => {
            return {
              request_batch_id: batch.id ?? batch.request_batch_id ?? batch.batch_id,
              request_item_id: item.id,
              quantity_received: item.quantity_received
            };
          })
          .filter(Boolean);
      })
    }),
    [transferToUpdate.items]
  );

  useEffect(() => {
    const disable = transferToUpdate.items.some((item) => {
      const totalSent = (item.batches ?? []).reduce(
        (sum: number, batch: any) => sum + (Number(batch.quantity) || 0),
        0
      );

      if (transferToUpdate.status === ENUM_TRANSFER_STATUS.SENT) {
        return Number(item.quantity_received || 0) > totalSent;
      }

      return totalSent > Number(item.quantity || 0);
    });

    setIsUpdateTransferDisabled(disable);
  }, [transferToUpdate.items, transferToUpdate.status]);

  const sendItems = async () => {
    try {
      setIsUpdatingTransfer(true);
      const response = await apiService.put(
        `main-store-transfer/${transferTypeParam}/${transferToUpdate.id}/send`,
        {
          data: sendPayload,
          version: 'v2'
        }
      );
      if (response) {
        toast(<ToastMessage message='Transfer request updated successfully' type='success' />);
        router(transferListPath);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating transfer request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingTransfer(false);
    }
  };

  const rejectTransfer = async () => {
    if (!activeStore) return;
    try {
      setIsUpdatingTransfer(true);
      const response = await apiService.post(
        `main-store-transfer/${activeStore.id}/transfer-requests/${transferToUpdate.id}/reject`
      );
      if (response) {
        toast(<ToastMessage message='Transfer request updated successfully' type='success' />);
        router(transferListPath);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating transfer request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingTransfer(false);
    }
  };

  const receiveItems = async () => {
    try {
      setIsUpdatingTransfer(true);
      const response = await apiService.put(
        `main-store-transfer/${transferTypeParam}/${transferToUpdate.id}/receive`,
        {
          data: receivePayload,
          version: 'v2'
        }
      );
      if (response) {
        toast(<ToastMessage message='Transfer items received successfully' type='success' />);
        router(-1);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating transfer request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingTransfer(false);
    }
  };

  const onConfirm = () => {
    if (transferToUpdate.status === ENUM_TRANSFER_STATUS.SENT && isRequestingStore) {
      receiveItems();
      return;
    }
    if (transferToUpdate.status === ENUM_TRANSFER_STATUS.REQUESTED && !isRequestingStore) {
      sendItems();
    }
  };

  const canSendRequested = transferToUpdate.status === ENUM_TRANSFER_STATUS.REQUESTED;
  const canReceiveSent = transferToUpdate.status === ENUM_TRANSFER_STATUS.SENT;
  const canConfirmAction = canSendRequested || canReceiveSent;
  const confirmActionText = canReceiveSent
    ? 'Mark as received'
    : canSendRequested
    ? 'Send Items'
    : '';

  return (
    <RequestDetailsWrapper
      codeId={transferToUpdate.code || ''}
      statusTag={<TransferStatusTag type={transferToUpdate.status} />}
      header='Update request'
      subHeader='State the quantity received in good condition where necessary'>
      {loadingTransferDetails ? (
        <div className='flex justify-center items-center w-full h-[300px]'>
          <p>Loading transfer details...</p>
        </div>
      ) : (
        <>
          <div className='flex justify-between items-center px-[28px]'>
            {!isRequestingStore && (
              <div className='ml-auto'>
                <Button className='text-sm' color='primary' variant='link' onClick={rejectTransfer}>
                  Reject Transfer
                </Button>
              </div>
            )}
          </div>

          <div>
            {transferToUpdate.items.map((item, index) => (
              <TransferRequestItemDetails
                key={index}
                data={item}
                transferToUpdate={transferToUpdate}
                setTransferToUpdate={setTransferToUpdate}
              />
            ))}
          </div>

          <div>
            <hr className='border-t-4 border-[#F7F7F8]' />
            <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
              <Button variant='link' color='gray' small onClick={() => router(-1)}>
                Back
              </Button>

              {canConfirmAction && (
                <Button
                  variant='block'
                  color='primary'
                  small
                  className='w-[153px] h-[43px] px-4'
                  onClick={onConfirm}
                  loading={isUpdatingTransfer}
                  disabled={isUpdateTransferDisabled}>
                  {confirmActionText}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </RequestDetailsWrapper>
  );
};

export default TransferRequestDetails;
